/** Readings, weather, time and helpers: what sensors report and what HA schedules. */
import { c, dot, rr, type Glyph } from './geom.js';

const DROP = 'M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11z';
const SMALL_DROP = 'M12 3s5 5.5 5 9a5 5 0 0 1-10 0c0-3.5 5-9 5-9z';
const PUDDLE = 'M3 20.5c1.5 0 1.5-1 3-1s1.5 1 3 1 1.5-1 3-1 1.5 1 3 1 1.5-1 3-1 1.5 1 3 1';
const CLOUD = 'M7 18a4.5 4.5 0 0 1-.5-9 6 6 0 0 1 11.5 1.5A3.75 3.75 0 0 1 17.5 18z';
const CLOUD_HI = 'M7 15a4.5 4.5 0 0 1-.5-9 6 6 0 0 1 11.5 1.5A3.75 3.75 0 0 1 17.5 15z';
const SUN_RAYS = 'M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4';
const CALENDAR = `${rr(3, 5, 18, 16, 2.5)}M3 10h18M8 3v4M16 3v4`;

export const READINGS: Record<string, Glyph> = {
  // ---- environment
  thermometer: `M10 13.5V5a2 2 0 0 1 4 0v8.5a4 4 0 1 1-4 0zM12 10v6.5M17 6h2M17 9h2M17 12h1.5`,
  humidity: `${DROP}M9.5 16.5l5-5${dot(9.75, 12)}${dot(14.25, 16)}`,
  droplet: DROP,
  leak: { d: `${SMALL_DROP}${PUDDLE}`, g: SMALL_DROP },
  'leak-dry': `${SMALL_DROP}M3 20.5h18`,
  co2: `M9.5 9a3.6 3.6 0 1 0 0 6${c(14, 12, 3)}M18.5 16.2a1.2 1.2 0 1 1 2.1.8l-2.1 2.4h2.6`,
  molecule: `${c(7, 8, 3)}${c(17, 9, 2.5)}${c(11, 17, 3)}M9.5 9.5 10.5 14M9.8 7.6l4.8.8M15.5 11l-2.5 3.8`,
  pressure: `${c(12, 12, 9)}M12 12l4-4${dot(12, 12)}M12 5.5v1M5.5 12h1M17.5 12h1M7.4 7.4l.7.7`,
  gauge: 'M4 18a9 9 0 1 1 16 0M12 14l4-5',
  lux: { d: `${c(12, 12, 4)}M12 3v2M12 19v2M3 12h2M19 12h2`, f: 'M12 8a4 4 0 0 1 0 8z' },
  wind: 'M3 8h10a3 3 0 1 0-3-3M3 12h15a3 3 0 1 1-3 3M3 16h7',
  pulse: 'M3 12h4l2-5 4 10 2-5h6',
  flask: 'M9.5 3h5M10 3v6l-5 9.5A1.7 1.7 0 0 0 6.5 21h11a1.7 1.7 0 0 0 1.5-2.5L14 9V3M7.5 15h9',

  // ---- weather & sky
  sun: `${c(12, 12, 4)}${SUN_RAYS}`,
  moon: 'M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z',
  cloud: CLOUD,
  'cloud-sun': 'M8 3v1.5M3.5 8H2M4.5 4.5l1 1M13 5l-1 1M5.5 10.5A3.5 3.5 0 0 1 11.6 8M9 20a4 4 0 0 1-.5-8 5.5 5.5 0 0 1 10.5 1.5A3.3 3.3 0 0 1 18.5 20z',
  rain: `${CLOUD_HI}M8 18l-1 2.5M12 18l-1 2.5M16 18l-1 2.5`,
  snow: `${CLOUD_HI}${dot(8, 19)}${dot(12, 20)}${dot(16, 19)}${dot(10, 22)}${dot(14, 22)}`,
  storm: `${CLOUD_HI}M12 14l-2 4h4l-2 4`,
  'storm-rain': `${CLOUD_HI}M12.5 14l-2 3.5h3.5l-2 4M7.5 17.5l-1 2.5M18 17.5l-1 2.5`,
  'rain-heavy': `${CLOUD_HI}M7 17.5l-1.5 4M11 17.5l-1.5 4M15 17.5l-1.5 4M19 17.5l-1.2 3`,
  sleet: `${CLOUD_HI}M8 18l-1 2.5${dot(12, 18.5)}${dot(11, 21.5)}M16 18l-1 2.5`,
  hail: `${CLOUD_HI}M8 17.5l-.5 1.4${dot(7.2, 21.3)}M12.5 17.5l-.5 1.4${dot(11.7, 21.3)}M17 17.5l-.5 1.4${dot(16.2, 21.3)}`,
  'cloud-moon': 'M7.7 9A4.6 4.6 0 0 1 7.7 3A5.5 5.5 0 0 0 4.9 13M9 20a4 4 0 0 1-.5-8 5.5 5.5 0 0 1 10.5 1.5A3.3 3.3 0 0 1 18.5 20z',
  fog: 'M4 9h16M3 13h18M5 17h14',
  sunrise: 'M3 19h18M7 19a5 5 0 0 1 10 0M12 3v6M9.5 5.5 12 3l2.5 2.5M4.5 14l1.3.7M19.5 14l-1.3.7',
  sunset: 'M3 19h18M7 19a5 5 0 0 1 10 0M12 3v6M9.5 6.5 12 9l2.5-2.5M4.5 14l1.3.7M19.5 14l-1.3.7',
  compass: { d: `${c(12, 12, 9)}M15.5 8.5l-2 5-5 2 2-5z`, f: 'M15.5 8.5l-5 2 3 3z' },
  pin: `M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z${c(12, 10, 2.3)}`,

  // ---- time
  clock: `${c(12, 12, 9)}M12 7v5l3 2`,
  calendar: CALENDAR,
  schedule: `M3 10h13M7 3v4M13 3v4M16 8V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h5${c(17, 17, 4.5)}M17 15v2l1.2 1.2`,
  timer: `${c(12, 13, 8)}M12 9v4l2.5 2M10 2h4`,

  // ---- helpers & system
  input: `${rr(3, 7, 18, 10, 2)}M7 10v4`,
  select: `${rr(3, 7, 18, 10, 2)}M6.5 12h5M14.5 11l2 2 2-2`,
  number: `M4 12h16${c(9, 12, 2.5)}M4 7h.01M20 7h.01M4 17h.01M20 17h.01`,
  palette: `M12 3a9 9 0 1 0 0 18c1.5 0 2-1 2-2s-1-1.5-1-2.5 1-1.5 2-1.5h2a4 4 0 0 0 4-4c0-4.5-4-8-9-8z${c(7.5, 11, 1)}${c(10.5, 7, 1)}${c(15, 7.5, 1)}`,
  scene: { d: 'M10 4l1.6 4.4L16 10l-4.4 1.6L10 16l-1.6-4.4L4 10l4.4-1.6zM18 14l.8 2.2 2.2.8-2.2.8L18 20l-.8-2.2L15 17l2.2-.8z' },
  script: 'M8 6 3 12l5 6M16 6l5 6-5 6',
  update: 'M20 12a8 8 0 1 1-2.3-5.7M20 4v4h-4',
  'update-on': { d: 'M20 12a8 8 0 1 1-2.3-5.7M20 4v4h-4M12 8v4.5', f: c(12, 16, 1.3) },
  event: `${c(12, 12, 3)}M12 3v3M12 18v3M3 12h3M18 12h3`,
  currency: 'M16.5 6.5A3.5 3.5 0 0 0 10 8v4.5c0 3-1 5-3 6.5h11M7 12.5h7',
  star: 'M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1.1 5.8L12 16.8l-5.3 2.8 1.1-5.8-4.3-4.1 5.9-.8z',
  target: `${c(12, 12, 8.5)}${c(12, 12, 4.5)}${dot(12, 12)}`,
  generic: `${rr(4.5, 4.5, 15, 15, 4)}${dot(12, 12)}`,
};
